import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { MapPin, Users, Armchair, Smartphone, Calendar, Lock } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const AllocationDialog = ({ open, onOpenChange, onSave, propertyId, guests, staff, seats }) => {
  const [formData, setFormData] = useState({
    roomNumber: '',
    fbManagerId: '',
    seatIds: [],
    deviceIds: [],
    numAdults: 0,
    numKids: 0
  });
  const [loading, setLoading] = useState(false);
  const [guestInfo, setGuestInfo] = useState(null);
  const [allocatedSeats, setAllocatedSeats] = useState([]);
  const [allocatedDevices, setAllocatedDevices] = useState([]);
  const [seatTypes, setSeatTypes] = useState([]);
  const [devices, setDevices] = useState([]);
  const [sections, setSections] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sectionStaff, setSectionStaff] = useState({ attendants: [], servers: [] });
  const [staffErrors, setStaffErrors] = useState({ attendant: false, server: false });
  const [pinEnabled, setPinEnabled] = useState(false);
  const [pin, setPin] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    if (!open) {
      setFormData({
        roomNumber: '',
        fbManagerId: '',
        seatIds: [],
        deviceIds: [],
        numAdults: 0,
        numKids: 0
      });
      setGuestInfo(null);
      setAllocatedSeats([]);
      setAllocatedDevices([]);
      setSectionStaff({ attendants: [], servers: [] });
      setStaffErrors({ attendant: false, server: false });
      setPinEnabled(false);
      setPin('');
    } else {
      // Fetch allocated seats/devices, seat types, and devices when dialog opens
      // Use current date for real-time allocation
      const currentDate = new Date().toISOString().split('T')[0];
      fetchAllocatedSeats(currentDate);
      fetchAllocatedDevices(currentDate);
      fetchSeatTypes();
      fetchDevices();
      fetchSections();
      fetchRoles();
    }
  }, [open]);

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/sections/${propertyId}`);
      if (response.data.success) {
        setSections(response.data.sections || []);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/roles`);
      if (response.data.success) {
        setRoles(response.data.roles || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  // Fetch staff assigned to selected seats' sections
  const fetchSectionStaff = async (selectedSeatIds) => {
    if (selectedSeatIds.length === 0) {
      setSectionStaff({ attendants: [], servers: [] });
      setStaffErrors({ attendant: false, server: false });
      return;
    }

    try {
      // Get sections for selected seats
      const sectionIds = new Set();
      for (const seatId of selectedSeatIds) {
        const seat = seats.find(s => s.id === seatId);
        if (seat && seat.sectionId) {
          sectionIds.add(seat.sectionId);
        }
      }

      let allAttendants = [];
      let allServers = [];

      for (const sectionId of sectionIds) {
        // Fetch attendants for this section
        const attendantResponse = await axios.get(
          `${BACKEND_URL}/api/staff-section-assignments/section/${sectionId}/by-role/Pool And Beach Attendant`
        );
        if (attendantResponse.data.success) {
          allAttendants = [...allAttendants, ...attendantResponse.data.staff];
        }

        // Fetch servers for this section
        const serverResponse = await axios.get(
          `${BACKEND_URL}/api/staff-section-assignments/section/${sectionId}/by-role/Pool and Beach Server`
        );
        if (serverResponse.data.success) {
          allServers = [...allServers, ...serverResponse.data.staff];
        }
      }

      // Remove duplicates
      const uniqueAttendants = allAttendants.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
      const uniqueServers = allServers.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);

      setSectionStaff({
        attendants: uniqueAttendants,
        servers: uniqueServers
      });

      setStaffErrors({
        attendant: uniqueAttendants.length === 0,
        server: uniqueServers.length === 0
      });

    } catch (error) {
      console.error('Error fetching section staff:', error);
    }
  };

  const fetchSeatTypes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/seat-types/${propertyId}`);
      if (response.data.success) {
        setSeatTypes(response.data.seatTypes);
      }
    } catch (error) {
      console.error('Error fetching seat types:', error);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/devices/${propertyId}`);
      if (response.data.success) {
        setDevices(response.data.devices.filter(d => d.enabled)); // Only enabled devices
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  const fetchAllocatedSeats = async (date) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/allocations/${propertyId}/allocated-seats?date=${date}`
      );
      if (response.data.success) {
        setAllocatedSeats(response.data.allocatedSeatIds);
      }
    } catch (error) {
      console.error('Error fetching allocated seats:', error);
    }
  };

  const fetchAllocatedDevices = async (date) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/allocations/${propertyId}/allocated-devices?date=${date}`
      );
      if (response.data.success) {
        setAllocatedDevices(response.data.allocatedDeviceIds);
      }
    } catch (error) {
      console.error('Error fetching allocated devices:', error);
    }
  };

  const handleRoomNumberChange = (e) => {
    const roomNum = e.target.value;
    setFormData(prev => ({ ...prev, roomNumber: roomNum }));

    // Find guest by room number
    const guest = guests.find(g => g.roomNumber === roomNum);
    if (guest) {
      setGuestInfo(guest);
    } else {
      setGuestInfo(null);
    }
  };

  const handleManagerChange = (value) => {
    setFormData(prev => ({ ...prev, fbManagerId: value }));
  };

  const handleSeatToggle = (seatId) => {
    setFormData(prev => {
      const isRemoving = prev.seatIds.includes(seatId);
      const seatIds = isRemoving
        ? prev.seatIds.filter(id => id !== seatId)
        : [...prev.seatIds, seatId];
      
      // Fetch staff for the selected seats' sections
      fetchSectionStaff(seatIds);
      
      // Auto-add/remove device IDs from selected seats
      const seat = seats.find(s => s.id === seatId);
      let deviceIds = [...prev.deviceIds];
      
      if (seat && seat.staticDeviceId) {
        if (isRemoving) {
          // Remove device if no other selected seat uses it
          const otherSeatsWithDevice = seatIds
            .filter(id => id !== seatId)
            .some(id => {
              const s = seats.find(seat => seat.id === id);
              return s && s.staticDeviceId === seat.staticDeviceId;
            });
          
          if (!otherSeatsWithDevice) {
            deviceIds = deviceIds.filter(id => id !== seat.staticDeviceId);
          }
        } else {
          // Add device if not already in list
          if (!deviceIds.includes(seat.staticDeviceId)) {
            deviceIds = [...deviceIds, seat.staticDeviceId];
          }
        }
      }
      
      return { ...prev, seatIds, deviceIds };
    });
  };

  const handleDeviceToggle = (deviceId) => {
    setFormData(prev => {
      const deviceIds = prev.deviceIds.includes(deviceId)
        ? prev.deviceIds.filter(id => id !== deviceId)
        : [...prev.deviceIds, deviceId];
      return { ...prev, deviceIds };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.roomNumber.trim()) {
      toast({
        title: "Validation Error",
        description: "Room number is required",
        variant: "destructive"
      });
      return;
    }

    if (!guestInfo) {
      toast({
        title: "Validation Error",
        description: `No guest found in room number ${formData.roomNumber}`,
        variant: "destructive"
      });
      return;
    }

    if (!formData.fbManagerId) {
      toast({
        title: "Validation Error",
        description: "Please select a Food & Beverage Manager",
        variant: "destructive"
      });
      return;
    }

    if (formData.seatIds.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one seat",
        variant: "destructive"
      });
      return;
    }

    if (pinEnabled && pin.length !== 4) {
      toast({
        title: "Validation Error",
        description: "PIN must be exactly 4 digits",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    // Use current date/time for real-time allocation
    const currentDate = new Date().toISOString().split('T')[0];
    
    await onSave({
      propertyId,
      roomNumber: formData.roomNumber,
      fbManagerId: formData.fbManagerId,
      seatIds: formData.seatIds,
      deviceIds: formData.deviceIds,
      allocationDate: currentDate,
      numAdults: parseInt(formData.numAdults) || 0,
      numKids: parseInt(formData.numKids) || 0,
      pinEnabled: pinEnabled,
      pin: pinEnabled ? pin : null
    });
    
    setLoading(false);
  };

  // Filter staff to only show Pool and Beach Managers
  const managerRole = roles.find(r => 
    r.name?.toLowerCase().includes('pool and beach manager') ||
    r.name?.toLowerCase().includes('f&b manager') ||
    r.name?.toLowerCase().includes('food and beverage manager')
  );
  const fbManagers = managerRole 
    ? staff.filter(s => s.roleId === managerRole.id)
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle>Add Allocation</DialogTitle>
              <DialogDescription>
                Assign seats to a guest with F&B Manager
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Left Column */}
            <div className="space-y-4">
            {/* Room Number */}
            <div className="space-y-2">
              <Label htmlFor="roomNumber" className="text-slate-700 font-medium">
                Room Number *
              </Label>
              <Input
                id="roomNumber"
                value={formData.roomNumber}
                onChange={handleRoomNumberChange}
                placeholder="Enter room number"
                required
              />
              {guestInfo && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-green-900">{guestInfo.guestName}</p>
                    {guestInfo.category && (
                      <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
                        {guestInfo.category}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {formData.roomNumber && !guestInfo && (
                <p className="text-sm text-red-600">⚠️ No guest found in room {formData.roomNumber}</p>
              )}
            </div>

            {/* Number of Adults and Kids - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="numAdults" className="text-slate-700 font-medium">
                  Number of Adults
                </Label>
                <Input
                  id="numAdults"
                  type="number"
                  min="0"
                  value={formData.numAdults}
                  onChange={(e) => setFormData(prev => ({ ...prev, numAdults: e.target.value }))}
                  placeholder="Adults"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="numKids" className="text-slate-700 font-medium">
                  Number of Kids
                </Label>
                <Input
                  id="numKids"
                  type="number"
                  min="0"
                  value={formData.numKids}
                  onChange={(e) => setFormData(prev => ({ ...prev, numKids: e.target.value }))}
                  placeholder="Kids"
                />
              </div>
            </div>

            {/* F&B Manager */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">
                Food & Beverage Manager *
              </Label>
              <Select value={formData.fbManagerId} onValueChange={handleManagerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select F&B Manager" />
                </SelectTrigger>
                <SelectContent>
                  {fbManagers.length === 0 ? (
                    <div className="px-2 py-4 text-sm text-slate-500 text-center">
                      No Pool and Beach Managers found.
                    </div>
                  ) : (
                    fbManagers.map((manager) => (
                      <SelectItem key={manager.id} value={manager.id}>
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4" />
                          <span>{manager.name}</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Device Selection */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">
                Assign Device ({formData.deviceIds.length} selected)
              </Label>
              <div className="border border-slate-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                {devices.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-2">No devices available</p>
                ) : (
                  <div className="space-y-2">
                    {devices.filter(device => device.enabled).map((device) => {
                      const isAllocated = allocatedDevices.includes(device.id);
                      const isSelected = formData.deviceIds.includes(device.id);
                      
                      return (
                        <button
                          key={device.id}
                          type="button"
                          onClick={() => !isAllocated && handleDeviceToggle(device.id)}
                          disabled={isAllocated}
                          className={`w-full flex items-center justify-between p-2 rounded-lg border transition-all ${
                            isSelected
                              ? 'bg-blue-500 border-blue-500 text-white'
                              : isAllocated
                              ? 'bg-red-100 border-red-300 text-red-400 cursor-not-allowed'
                              : 'bg-white border-slate-300 text-slate-700 hover:border-blue-300'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <Smartphone className="w-4 h-4" />
                            <span className="text-sm font-medium">{device.deviceId}</span>
                          </div>
                          {isAllocated && <span className="text-xs">In Use</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* PIN Security */}
            <div className="space-y-3 pt-4 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Lock className="w-4 h-4 text-slate-500" />
                  <Label htmlFor="pin-toggle" className="text-slate-700 font-medium">
                    Enable PIN for Ordering
                  </Label>
                </div>
                <Switch
                  id="pin-toggle"
                  checked={pinEnabled}
                  onCheckedChange={setPinEnabled}
                  data-testid="pin-toggle"
                />
              </div>
              <p className="text-xs text-slate-500">
                When enabled, guests will need this PIN to place orders.
              </p>
              
              {pinEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="pin" className="text-slate-700 font-medium">
                    4-Digit PIN *
                  </Label>
                  <Input
                    id="pin"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={pin}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setPin(value);
                    }}
                    placeholder="Enter 4-digit PIN"
                    className="text-center text-2xl tracking-widest font-mono"
                    data-testid="pin-input"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {/* Real-time Allocation Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                <strong>Real-time:</strong>&nbsp;Current date/time will be used
              </p>
            </div>

            {/* Seat Selection */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">
                Select Seats * ({formData.seatIds.length} selected)
              </Label>
              <div className="border border-slate-300 rounded-lg p-3 max-h-72 overflow-y-auto">
                {seats.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No seats available</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {seats.filter(seat => seat.status !== 'Blocked').map((seat) => {
                      const isAllocated = allocatedSeats.includes(seat.id);
                      const isSelected = formData.seatIds.includes(seat.id);
                      const seatType = seatTypes.find(st => st.id === seat.seatTypeId);
                      
                      return (
                        <button
                          key={seat.id}
                          type="button"
                          onClick={() => !isAllocated && handleSeatToggle(seat.id)}
                          disabled={isAllocated}
                          className={`flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all min-h-[70px] ${
                            isSelected
                              ? 'bg-blue-500 border-blue-500 text-white'
                              : isAllocated
                              ? 'bg-red-100 border-red-300 text-red-400 cursor-not-allowed'
                              : 'bg-white border-slate-300 text-slate-700 hover:border-blue-300'
                          }`}
                        >
                          {seatType && seatType.icon ? (
                            <img 
                              src={seatType.icon} 
                              alt={seatType.name}
                              className={`w-6 h-6 object-contain mb-1 ${
                                isSelected ? 'brightness-0 invert' : isAllocated ? 'opacity-50' : ''
                              }`}
                            />
                          ) : (
                            <Armchair className="w-5 h-5 mb-1" />
                          )}
                          <span className="text-xs font-semibold">{seat.seatNumber}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span>Selected</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
                  <span>Allocated</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-white border border-slate-300 rounded"></div>
                  <span>Available</span>
                </div>
              </div>
            </div>

            {/* Staff Assignment Info */}
            {formData.seatIds.length > 0 && (sectionStaff.attendants.length > 0 || sectionStaff.servers.length > 0) && (
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Staff Assigned</Label>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">Attendant:</span>
                    {sectionStaff.attendants.length > 0 ? (
                      <span className="text-xs text-green-600 font-medium">
                        {sectionStaff.attendants.map(s => s.name).join(', ')}
                      </span>
                    ) : (
                      <span className="text-xs text-red-600 font-medium">None</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">Server:</span>
                    {sectionStaff.servers.length > 0 ? (
                      <span className="text-xs text-green-600 font-medium">
                        {sectionStaff.servers.map(s => s.name).join(', ')}
                      </span>
                    ) : (
                      <span className="text-xs text-red-600 font-medium">None</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Staff Errors */}
            {formData.seatIds.length > 0 && (staffErrors.attendant || staffErrors.server) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
                {staffErrors.attendant && (
                  <p className="text-xs text-red-700">⚠️ No Attendant assigned to section</p>
                )}
                {staffErrors.server && (
                  <p className="text-xs text-red-700">⚠️ No Server assigned to section</p>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !guestInfo}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Creating...
                </>
              ) : (
                'Create Allocation'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
